class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.scheduleShippingEstimateUpdate();
    });
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.shippingEstimateAbortController?.abort();
    if (this.shippingEstimateUpdateFrame) cancelAnimationFrame(this.shippingEstimateUpdateFrame);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.scheduleShippingEstimateUpdate();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.scheduleShippingEstimateUpdate();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  scheduleShippingEstimateUpdate() {
    if (this.shippingEstimateUpdateFrame) cancelAnimationFrame(this.shippingEstimateUpdateFrame);
    this.shippingEstimateUpdateFrame = requestAnimationFrame(() => {
      this.shippingEstimateUpdateFrame = undefined;
      this.updateShippingEstimate();
    });
  }

  async updateShippingEstimate() {
    this.shippingEstimateAbortController?.abort();

    const estimateElement = this.querySelector('[data-cart-shipping-estimate]');
    const availabilityElements = Array.from(this.querySelectorAll('[data-cart-item-availability]'));
    if (!estimateElement || availabilityElements.length === 0) return;

    const estimateValue = estimateElement.querySelector('[data-cart-shipping-estimate-value]');
    const estimateMessage = estimateElement.querySelector('[data-cart-shipping-estimate-message]');
    const items = availabilityElements.map((element) => ({
      variantId: element.dataset.variantId,
      quantity: Number(element.dataset.quantity || 0),
    }));

    estimateValue.textContent = 'Calculating…';
    estimateMessage.hidden = true;
    availabilityElements.forEach((element) => this.setItemAvailability(element, 'loading'));

    const controller = new AbortController();
    this.shippingEstimateAbortController = controller;

    try {
      const response = await fetch('/apps/shipping-estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not calculate the shipping estimate.');

      estimateValue.textContent = this.formatShippingDateRange(data.shipFrom, data.shipTo);
      estimateMessage.hidden = !data.hasMixedAvailability;

      const availabilityByVariant = new Map(
        (data.lines || []).map((line) => [this.numericVariantId(line.variantId), line.availability])
      );
      availabilityElements.forEach((element) => {
        this.setItemAvailability(
          element,
          availabilityByVariant.get(this.numericVariantId(element.dataset.variantId)) || 'unknown'
        );
      });

    } catch (error) {
      if (error.name === 'AbortError') return;
      estimateValue.textContent = 'Unavailable';
      estimateMessage.hidden = true;
      availabilityElements.forEach((element) => this.setItemAvailability(element, 'unknown'));
      console.error('Could not load the cart shipping estimate.', error);
    }
  }

  setItemAvailability(element, availability) {
    const presentations = {
      in_stock: { label: 'in stock', modifier: 'in-stock' },
      preorder: { label: 'pre-order', modifier: 'preorder' },
      backorder: { label: 'backorder', modifier: 'backorder' },
      loading: { label: 'checking availability…', modifier: 'loading' },
      unknown: { label: 'availability unavailable', modifier: 'unknown' },
    };
    const presentation = presentations[availability] || presentations.unknown;

    element.classList.remove(
      'cart-item__availability--in-stock',
      'cart-item__availability--preorder',
      'cart-item__availability--backorder',
      'cart-item__availability--loading',
      'cart-item__availability--unknown'
    );
    element.classList.add(`cart-item__availability--${presentation.modifier}`);
    element.textContent = presentation.label;
  }

  formatShippingDateRange(shipFrom, shipTo) {
    if (!shipFrom && !shipTo) return 'Unavailable';
    if (!shipFrom || shipFrom === shipTo) return this.formatShippingDate(shipFrom || shipTo);
    if (!shipTo) return this.formatShippingDate(shipFrom);
    return `${this.formatShippingDate(shipFrom)} – ${this.formatShippingDate(shipTo)}`;
  }

  formatShippingDate(value) {
    const date = new Date(`${value}T00:00:00Z`);
    if (!Number.isFinite(date.getTime())) return 'Unavailable';
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  numericVariantId(value) {
    return String(value || '').split('/').pop();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    return super.onCartUpdate().then(() => {
      document.querySelector('cart-drawer')?.scheduleShippingEstimateUpdate();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
